import React, { useContext, useEffect, useState } from "react"
import AppContext from "../../../../shared/AppContext"
import Sectors from "../sectors"
import Rating, { RatingsPanel } from "../rating"
import Takeaways from "../takeaways"
import Outlook from "../outlook"
import Button from "../../../../components/Button"
import HoverTab, { HoverTabPanel } from "../HoverTabPanel"
import Favorite, { FavoriteButton } from "../../../../components/FavoriteButton"
import ModelPreviewImage, { ModelPreviewPanel } from "../modelPreview"
import { QuotePanel, Quote } from "../quote"
import { getProduct } from "../../queries"
import { Query } from "react-apollo"
import { Redirect } from "react-router"

import "./style.scss"

const netScoreTip = () => {
  return (
    <div>
      <p className="header">
        <span>NET SCORE</span> Represents the intensity of spend for a vendor.
        Higher Net scores indicate a positive spend trajectory, while lower Net
        scores indicate a flat or negative spend trajectory.
      </p>
      <p className="footer">
        <span>Net Score =</span>{" "}
        <b>[ Adoption % + Increase % ] - [ Decrease % + Replacing % ]</b>
      </p>
    </div>
  )
}

const marketShareTip = () => {
  return (
    <div>
      <p className="header">
        <span>MARKET SHARE</span> Represents the overall enterprise pervasion
        within ETR's sample.
      </p>
      <p className="footer">
        <span>Market Share =</span>{" "}
        <b>
          [ Vendor Citations excluding Replacements ] ÷ [ Sector Respondents ]
        </b>
      </p>
    </div>
  )
}

const tip = (percent, answer) => {
  let roundedPercent = ""
  let startOfStr = "indicates"
  if (percent) {
    startOfStr = "indicated"
    roundedPercent += Math.round(percent * 100) + "%"
  }
  switch (answer) {
    case "adoption":
      return (
        <div>
          <p className="header">
            <b>{roundedPercent}</b> <span>{percent ? "" : "ADOPTION"}</span>{" "}
            {startOfStr} new adoption to their organization.
          </p>
        </div>
      )
    case "increase":
      return (
        <div>
          <p className="header">
            <b>{roundedPercent}</b> <span>{percent ? "" : "INCREASE"}</span>{" "}
            {startOfStr} their organization plans an increased spend.
          </p>
        </div>
      )
    case "flat":
      return (
        <div>
          <p className="header">
            <b>{roundedPercent}</b> <span>{percent ? "" : "FLAT"}</span>{" "}
            {startOfStr} their organization plans to have a flat spend.
          </p>
        </div>
      )
    case "decrease":
      return (
        <div>
          <p className="header">
            <b>{roundedPercent}</b> <span>{percent ? "" : "DECREASE"}</span>{" "}
            {startOfStr} their organization plans a decreased spend.
          </p>
        </div>
      )
    case "replacing":
      return (
        <div>
          <p className="header">
            <b>{roundedPercent}</b> <span>{percent ? "" : "REPLACING"}</span>{" "}
            {startOfStr} their organization intends to replace, put in
            containment, remove or swap-out.
          </p>
        </div>
      )
    default:
      return
  }
}

const VendorPage = (props) => {
  const [isSectionExpanded, setExpandSectionState] = useState(false)

  const { trackPage, openInquireModal, serviceType } = useContext(AppContext)
  const { topic, viewer, history, settings } = props
  const userAccess = viewer.vendorPageAccess

  const {
    id,
    name,
    type,
    takeaways,
    ratings,
    sp500,
    global2000,
    fortune500,
    components,
    surveyRespondents,
    tsisReportURL,
    tsisSurveyAnswerDistribution,
    rampImages,
    outlook,
    logo,
    topicMetadata,
  } = topic

  const { isUserAuthenticated } = viewer

  useEffect(() => {
    trackPage("Topic Page: Vendor", {
      topicId: id,
      topicName: name,
      topicType: type.toUpperCase(),
    })
  }, [id, name, type])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  const { count } = surveyRespondents

  const renderSummaryCTAButton = () => {
    if (!shouldShowTakeaways()) {
      return null
    }

    const onGainAccessClick = () => {
      openInquireModal(
        serviceType.UpgradePlan,
        "Vendor Page: Gain Access Button"
      )
    }

    const onViewInfoClick = () => {
      setExpandSectionState(!isSectionExpanded)
    }

    let buttonText
    let onClickHandler
    let cypressData

    if (isUserAuthenticated && userAccess === "FULL") {
      buttonText = isSectionExpanded ? "View Less" : "View More"
      onClickHandler = onViewInfoClick
    } else {
      buttonText = "Gain Access"
      onClickHandler = onGainAccessClick
      cypressData = "vendor-page-gain-access"
    }

    return (
      <div className="separator-cta-button">
        <hr />
        <Button
          text={buttonText}
          action={onClickHandler}
          data-cy={cypressData}
        />
      </div>
    )
  }

  const hasNoAccess = userAccess === "NONE"
  const renderTopicComponents = () => {
    return components.map((component, idx) => {
      if (component.__typename.toLowerCase() === "image") {
        return (
          <ModelPreviewPanel
            name={name}
            title={component.title}
            subtitle={component.subtitle}
            key={idx}
          >
            <ModelPreviewImage
              link={component.slug}
              isAuthenticated={isUserAuthenticated}
              hasNoAccess={hasNoAccess}
              imageURL={component.imageURL}
              onCtaClick={() => {
                openInquireModal(
                  serviceType.UpgradePlan,
                  "Vendor Page: Get Access Button"
                )
              }}
              trackingLocation={"Vendor Page"}
              alt={component.subtitle}
            />
            {component.hasLegend && (
              <HoverTabPanel center>
                {Object.entries(tsisSurveyAnswerDistribution).map(
                  ([answer], index) => {
                    if (answer !== "__typename") {
                      return (
                        <HoverTab
                          key={index}
                          answer={answer}
                          percent={""}
                          dataTip={answer}
                          getContent={() => tip("", answer)}
                          disable={hasNoAccess}
                        />
                      )
                    }
                  }
                )}
                <HoverTab
                  answer="net score"
                  percent=""
                  dataTip="NET SCORE"
                  getContent={netScoreTip}
                  disable={hasNoAccess}
                />
                <HoverTab
                  answer="market share"
                  percent=""
                  dataTip="MARKET SHARE"
                  getContent={marketShareTip}
                  disable={hasNoAccess}
                />
              </HoverTabPanel>
            )}
          </ModelPreviewPanel>
        )
      }

      if (component.__typename.toLowerCase() === "quotes") {
        return (
          <QuotePanel vendorName={name} className="margin-bottom-30" key={idx}>
            {component.quotes.map((component, quoteIdx) => {
              return <Quote key={quoteIdx} quoteData={component} />
            })}
          </QuotePanel>
        )
      }
    })
  }

  const reportCTAButtonWithQuery = () => {
    const buttonAction = () =>
      openInquireModal(
        serviceType.RequestAVendorReport,
        "Vendor Page: View Report Button"
      )

    if (!tsisReportURL) {
      return (
        <Button
          className="report btn-primary"
          text={"Request Analysis"}
          action={buttonAction}
        />
      )
    }
    /**
     * Todo: return null when no arguments are provided instead of throwing an error
     */
    return (
      <Query
        query={getProduct}
        variables={{ slug: tsisReportURL }}
        fetchPolicy={"no-cache"}
      >
        {({ data }) => {
          const product = data ? data.product : null
          const action = () => {
            if (
              product &&
              (product.isSpecialAccess || product.isPurchased || product.free)
            ) {
              history.push(`/marketplace/${tsisReportURL}/view`)
            } else if (product) {
              history.push(`/marketplace/${tsisReportURL}`)
            } else {
              buttonAction()
            }
          }

          return (
            <Button
              data-cy="view-report"
              className="report btn-primary"
              text={product ? "View Report" : "Request Analysis"}
              action={action}
            />
          )
        }}
      </Query>
    )
  }

  const handleSurveyCommentary = () => {
    if (viewer.canSearchVennTranscripts) {
      history.push(`/etr-insights/search?vendor=${name}`)
    } else {
      openInquireModal(
        serviceType.AccessRespondentCommentary,
        "Vendor Page: View Respondent Commentary Button"
      )
    }
  }

  const shouldShowRatings = () => {
    if (!ratings || !ratings.length) {
      return false
    }
    return !ratings.every((rating) => {
      if (!rating || !rating.value) {
        return true
      }
      return rating.value.toLowerCase() === "no rating"
    })
  }

  const shouldShowTakeaways = () => !!(takeaways && takeaways.length)
  const shouldShowOutlook = () => !!(outlook.title && outlook.text)

  const isExpanded =
    isUserAuthenticated && userAccess === "FULL" ? isSectionExpanded : true

  if (!props.viewer || !props.viewer.isUserAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: "/login",
          state: { from: props.history.location.pathname },
        }}
      />
    )
  }

  const singleVendorAggregateComponent = components.find((component) => {
    return component.subtitle === "Single Vendor Aggregate"
  })

  const singleVendorAggregateUrl =
    (singleVendorAggregateComponent && singleVendorAggregateComponent.slug) ||
    "/research/Models/Time-Series?page=SINGLE%20VENDOR%20AGGREGATE&tutorial=false"

  return (
    <React.Fragment>
      <div className="vendor-page">
        {settings.showVendorPageBanner && (
          <div className="vendor-update-banner alert alert-danger" role="alert">
            {`${settings.vendorPageBannerMessage}`.trim()} Click{" "}
            <a href={`${singleVendorAggregateUrl}`} className="alert-link">
              HERE
            </a>{" "}
            to access the latest data.
          </div>
        )}

        <header className="vendor-header margin-bottom-30">
          <section className="logo">
            <img src={logo} alt={name + "-logo"} />
          </section>
          <section className="vendor-header-content">
            <div>
              <h1>{name}</h1>
              <Sectors sectors={topicMetadata.sectorsTracked || []} />
            </div>

            {isUserAuthenticated && (
              <div>
                <Favorite
                  topic={props.topic}
                  location={{ search: {} }}
                  favoriteType="VENDOR"
                  trackingLocation="Vendor Page"
                  variables={{
                    topicId: id,
                  }}
                >
                  <FavoriteButton />
                </Favorite>
              </div>
            )}
          </section>
        </header>
        <article className="vendor-summary" data-expanded={isExpanded}>
          <section className="rating-outlook">
            {shouldShowRatings() && (
              <RatingsPanel>
                {ratings.map((rating, idx) => {
                  return (
                    <Rating
                      key={idx}
                      rating={rating}
                      isAuthenticated={isUserAuthenticated}
                      userAccess={userAccess}
                    />
                  )
                })}
              </RatingsPanel>
            )}
            {shouldShowOutlook() && (
              <Outlook
                outlook={outlook}
                isAuthenticated={isUserAuthenticated}
                userAccess={userAccess}
              />
            )}
          </section>
          {shouldShowTakeaways() && (
            <section className="vendor-takeaways" data-expanded={isExpanded}>
              <Takeaways
                takeaways={takeaways}
                isAuthenticated={isUserAuthenticated}
                userAccess={userAccess}
              />
            </section>
          )}
        </article>
        {renderSummaryCTAButton()}
        <div className="vendor-survey-summary  margin-bottom-30">
          <div className="respondent">
            <ul>
              <li className="survey-count">{count}</li>
              <li className="survey-name">Survey Respondents</li>
            </ul>
          </div>
          <div className="indexes">
            <ul>
              <li>
                <span className="index-value">{fortune500}</span>
                <span className="index-name">Fortune 500</span>
              </li>
              <li>
                <span className="index-value">{sp500}</span>
                <span className="index-name"> {"S&P"} 500</span>
              </li>
              <li>
                <span className="index-value">{global2000}</span>
                <span className="index-name">Global 2000</span>
              </li>
            </ul>
          </div>
          <div className="answer-distribution">
            <HoverTabPanel title="Distribution of the Survey Answers">
              {Object.entries(tsisSurveyAnswerDistribution).map(
                ([answer, percent], index) => {
                  if (answer !== "__typename") {
                    return (
                      <HoverTab
                        key={index}
                        answer={answer}
                        percent={percent}
                        dataTip={answer}
                        getContent={() => tip(percent, answer)}
                        disable={hasNoAccess}
                      />
                    )
                  }
                }
              )}
            </HoverTabPanel>
          </div>
        </div>
        <div className="survey-reports gradient-to-right margin-bottom-30">
          <div className="btn-container grid">
            {reportCTAButtonWithQuery()}
            <Button
              className="commentary btn-royal-blue"
              action={handleSurveyCommentary}
              text="View Respondent Commentary"
            />
          </div>
        </div>
        <div className="vendor-models">{renderTopicComponents()}</div>
        <div className="vendor-footer">
          <div className="ramp-cta-banner">
            <p className="title">
              Would you like to speak with our Survey Takers? Let us recommend
              the right one for you.
            </p>
            <div className="ramp-images grid">
              {rampImages.map((image, idx) => {
                return (
                  <img
                    className={`ramp-expert-image-${idx + 1}`}
                    key={idx}
                    src={image}
                  />
                )
              })}
            </div>
            <Button
              className="btn-ramp"
              text={"GET STARTED"}
              action={() => {
                openInquireModal(
                  serviceType.TalkToSurveyRespondents,
                  "Vendor Page: Get Started Button"
                )
              }}
            />
          </div>
          <div
            className="survey-cta-banner"
            style={{
              backgroundImage: `url("https://${process.env.S3BUCKET}.s3.amazonaws.com/images/flash_survey_triangle.png")`,
            }}
          >
            <p className="title">
              Want to dig deeper into the{" "}
              <span className="highlight">data</span>?
            </p>
            <p className="content">
              Uncover the details and the narrative behind the trends and
              relationships. Get an depth analysis into the reasoning &amp;
              driving factors behind the trends shaping the enterprise
              technology landscape.
            </p>
            <Button
              className="btn-primary"
              text={"CONDUCT A FLASH SURVEY"}
              action={() => {
                openInquireModal(
                  serviceType.ConductAFlashSurvey,
                  "Vendor Page: Conduct a Flash Survey Button"
                )
              }}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default VendorPage
